import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import KpiCard from '../components/KpiCard';
import { fmtMoney, fmtInt } from '../lib/fmt';
import type { ExecutiveSummaryOut, KPIsOut, PaginatedResponse, PedidoOut } from '../types/api';

const APROVADO_OU_PAGO = new Set(['Aprovado', 'Pago parcial', 'Pago total']);

export default function ConclusaoExecutiva() {
  const { data, isLoading } = useQuery<ExecutiveSummaryOut>({
    queryKey:['executive-summary'],
    queryFn:()=>api.get<ExecutiveSummaryOut>('/analysis/executive-summary').then(r=>r.data),
  });
  const { data: kpisExt } = useQuery<KPIsOut>({
    queryKey:['kpis'],
    queryFn:()=>api.get<KPIsOut>('/analysis/kpis').then(r=>r.data),
  });
  const { data: pedidosPage } = useQuery<PaginatedResponse<PedidoOut>>({
    queryKey:['pedidos-all'],
    queryFn:()=>api.get<PaginatedResponse<PedidoOut>>('/pedidos?limit=1000').then(r=>r.data),
  });
  const pedidos = pedidosPage?.items ?? [];

  if (isLoading) return <div className="text-mbc-mute text-center mt-10">Carregando…</div>;
  if (!data) return <div className="text-mbc-mute text-center mt-10">Nenhum dado processado. Vá para Importação.</div>;

  const { kpis, top_fornecedores, top_items } = data;
  const k = kpisExt ?? kpis;

  // Ciclo de reposição
  const skusEmPedido = new Set(pedidos.flatMap(p=>p.skus_json));
  const poAgAprov   = pedidos.filter(p=>p.status_po==='Aguardando aprovação');
  const poAprov     = pedidos.filter(p=>APROVADO_OU_PAGO.has(p.status_po));
  const poTrans     = pedidos.filter(p=>p.status_log==='Em trânsito');
  const poRecPa     = pedidos.filter(p=>p.status_log==='Recebido parcial');
  const poRecTot    = pedidos.filter(p=>p.status_po==='Recebido'||p.status_log==='Recebido total');

  const totSug = (k as KPIsOut).aprovados + (k as KPIsOut).negociar + (k as KPIsOut).bloqueados;
  const pctCobertura = totSug > 0 ? Math.round(100 * skusEmPedido.size / totSug) : 0;

  const topRisco = [...top_items].sort((a,b)=>Number(b.valor_perdido)-Number(a.valor_perdido)).slice(0,5);
  const topInvest = [...top_items].sort((a,b)=>b.investimento-a.investimento).slice(0,5);

  const PIPE = [
    { label:'Aguard. aprov', count:poAgAprov.length, valor:poAgAprov.reduce((s,p)=>s+Number(p.valor_total),0), color:'rgba(245,158,11,.08)', textColor:'text-amber-300' },
    { label:'Aprovados',     count:poAprov.length,   valor:poAprov.reduce((s,p)=>s+Number(p.valor_total),0),   color:'rgba(34,160,107,.08)', textColor:'text-emerald-300' },
    { label:'Em trânsito',   count:poTrans.length,   valor:poTrans.reduce((s,p)=>s+Number(p.valor_total),0),   color:'rgba(56,189,248,.08)', textColor:'text-sky-300' },
    { label:'Receb. parcial',count:poRecPa.length,   valor:poRecPa.reduce((s,p)=>s+Number(p.valor_total),0),  color:'rgba(34,160,107,.04)', textColor:'text-emerald-300' },
    { label:'Receb. total',  count:poRecTot.length,  valor:poRecTot.reduce((s,p)=>s+Number(p.valor_total),0), color:'rgba(34,160,107,.15)', textColor:'text-emerald-200' },
  ];

  return (
    <div className="space-y-6 fade-in">
      <div className="flex justify-end gap-2">
        <button onClick={()=>window.print()} className="text-sm bg-mbc-blue/30 text-mbc-bluel px-4 py-2 rounded-xl">🖨️ Imprimir / PDF</button>
      </div>

      <div className="card p-7 glow">
        <h2 className="text-white font-extrabold text-2xl">Conclusão Executiva — Compras MBC</h2>
        <p className="text-mbc-mute text-sm mt-1">
          Gerado automaticamente em {new Date().toLocaleString('pt-BR')} · base de {fmtInt(kpis.total_skus)} SKUs.
        </p>

        {/* 3 KPIs topo */}
        <div className="grid sm:grid-cols-3 gap-4 mt-5">
          <KpiCard label="Necessidade de compra" value={fmtMoney(k.valor_sugestao)} sub={`${fmtInt(totSug)} SKUs`} accent="mbc-orangel" />
          <KpiCard label="Faturamento em risco"  value={fmtMoney(kpis.valor_perdido)} sub="por ruptura"           accent="rose-300" />
          <KpiCard label="Itens críticos / zerados" value={fmtInt(kpis.criticos)}    sub={`${kpis.zerados} zerados`} accent="amber-300" />
        </div>

        {/* Ciclo de reposição */}
        <div className="mt-6 card p-4" style={{background:'rgba(10,77,158,.06)',borderColor:'rgba(30,111,214,.3)'}}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-mbc-bluel font-bold flex items-center gap-2">🔄 Ciclo de Reposição — status atual</h3>
            <span className="text-xs text-mbc-mute">{pedidos.length} pedido{pedidos.length!==1?'s':''} registrado{pedidos.length!==1?'s':''}</span>
          </div>
          <div className="mb-3">
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-mbc-soft"><strong>{pctCobertura}%</strong> das sugestões já viraram pedido</span>
              <span className="text-mbc-mute">{skusEmPedido.size} de {totSug} SKUs</span>
            </div>
            <div className="h-2 bg-mbc-line rounded overflow-hidden">
              <div className="h-2 bg-emerald-500 transition-all" style={{width:`${pctCobertura}%`}}></div>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
            {PIPE.map(p=>(
              <div key={p.label} className="card p-2" style={{background:p.color}}>
                <div className={`font-bold ${p.textColor}`}>{p.label}</div>
                <div className="text-white font-extrabold text-base mt-0.5">{p.count}</div>
                <div className="text-mbc-mute">{fmtMoney(p.valor)}</div>
              </div>
            ))}
          </div>
          {totSug > skusEmPedido.size
            ? <div className="mt-3 text-xs text-amber-300"><strong>⚠ {fmtInt(totSug - skusEmPedido.size)} SKUs com sugestão de compra ainda não viraram pedido</strong></div>
            : <div className="mt-3 text-xs text-emerald-300">✓ Todas as sugestões já estão cobertas por pedidos</div>}
        </div>

        {/* Narrativa */}
        <div className="mt-6 space-y-5 text-sm leading-relaxed text-mbc-soft">
          <div>
            <h3 className="text-mbc-orangel font-bold mb-1">1) Principais riscos</h3>
            <ul className="list-disc pl-5 text-mbc-soft/90 space-y-1">
              <li><span className="text-rose-300 font-bold">{fmtInt(kpis.zerados)} itens zerados</span> com venda ativa, <span className="text-amber-300 font-bold">{fmtInt(kpis.criticos)} em risco de ruptura</span>.</li>
              <li>Faturamento potencialmente perdido: <strong>{fmtMoney(kpis.valor_perdido)}</strong>.</li>
              {topRisco.map(i=><li key={i.sku}><strong>{i.sku}</strong> {i.nome.slice(0,45)} — {fmtMoney(Number(i.valor_perdido))} em risco.</li>)}
            </ul>
          </div>
          <div>
            <h3 className="text-mbc-orangel font-bold mb-1">2) Oportunidades</h3>
            <ul className="list-disc pl-5 text-mbc-soft/90 space-y-1">
              <li><span className="text-emerald-300 font-bold">{fmtInt(k.aprovados)} itens aprovados</span> (margem ≥ 10%), prontos para cotação.</li>
              <li>Marcas críticas: <strong>{top_fornecedores.slice(0,3).map(f=>f.nome).join(', ')||'—'}</strong>.</li>
              {kpis.excesso>0 && <li><span className="text-sky-300 font-bold">{fmtInt(kpis.excesso)} itens em excesso</span> — oportunidade de giro / promoção.</li>}
            </ul>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-mbc-orangel font-bold">3) Necessidades de negociação</h3>
              {(k as KPIsOut).negociar>0 && <span className="text-xs text-amber-300">{fmtInt((k as KPIsOut).negociar)} itens a negociar</span>}
            </div>
            <ul className="list-disc pl-5 text-mbc-soft/90 space-y-1">
              {(k as KPIsOut).negociar>0
                ? <li><span className="text-amber-300 font-bold">{fmtInt((k as KPIsOut).negociar)} itens</span> precisam reduzir custo para atingir margem mínima de 10%.</li>
                : <li>Nenhum item exige renegociação.</li>}
            </ul>
          </div>
          <div>
            <h3 className="text-mbc-orangel font-bold mb-1">4) Necessidades financeiras</h3>
            <ul className="list-disc pl-5 text-mbc-soft/90 space-y-1">
              <li>Investimento total sugerido: <strong>{fmtMoney(k.valor_sugestao)}</strong>.</li>
              <li>{pedidos.length>0?`${pedidos.length} pedidos registrados no sistema.`:'Nenhum PO registrado ainda.'}</li>
              <li>Priorizar caixa para os top {Math.min(5,topInvest.length)}: {topInvest.map(i=>i.sku).join(', ')||'—'}.</li>
            </ul>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-mbc-orangel font-bold">5) Itens bloqueados</h3>
              {(k as KPIsOut).bloqueados>0 && <span className="text-xs text-rose-300">{fmtInt((k as KPIsOut).bloqueados)} bloqueados</span>}
            </div>
            <ul className="list-disc pl-5 text-mbc-soft/90 space-y-1">
              {(k as KPIsOut).bloqueados>0
                ? <li><span className="text-rose-300 font-bold">{fmtInt((k as KPIsOut).bloqueados)} itens bloqueados</span> — custo acima do teto de margem mínima. Não comprar no custo atual.</li>
                : <li>Nenhum item bloqueado.</li>}
            </ul>
          </div>
          <div>
            <h3 className="text-mbc-orangel font-bold mb-1">6) Recomendações estratégicas</h3>
            <ul className="list-disc pl-5 text-mbc-soft/90 space-y-1">
              <li>Comprar primeiro curva A crítica, preservando margem mínima de 10%.</li>
              <li>Bloquear compras com margem projetada negativa.</li>
              <li>Consolidar pedidos por fornecedor.</li>
              <li>Snapshot diário para medir evolução.</li>
            </ul>
          </div>
          <div>
            <h3 className="text-mbc-orangel font-bold mb-1">7) Impacto projetado no faturamento</h3>
            <p>Repor itens sugeridos protege até <strong>{fmtMoney(kpis.valor_perdido)}</strong> em faturamento. Investimento de <strong>{fmtMoney(k.valor_sugestao)}</strong> sustenta giro das marcas líderes ({top_fornecedores.slice(0,3).map(f=>f.nome).join(', ')||'—'}).</p>
          </div>
        </div>
      </div>

      {/* Top 10 */}
      <div className="card overflow-hidden">
        <div className="px-5 py-3 border-b border-mbc-line">
          <span className="text-xs text-mbc-mute font-bold uppercase">Top 10 — Maior Valor Perdido</span>
        </div>
        <div className="scroller">
          <table className="dt">
            <thead>
              <tr>
                <th style={{width:90}}>SKU</th>
                <th className="text-left" style={{width:240}}>Produto</th>
                <th className="text-left" style={{width:160}}>Fornecedor</th>
                <th className="text-center" style={{width:52}}>Cls</th>
                <th className="num" style={{width:80}}>Estoque</th>
                <th className="num" style={{width:95}}>Cobertura</th>
                <th className="num" style={{width:90}}>Sugestão</th>
                <th className="num" style={{width:120}}>Valor perdido</th>
              </tr>
            </thead>
            <tbody>
              {topRisco.concat(top_items.slice(topRisco.length,10)).map(i=>(
                <tr key={i.sku}>
                  <td><span className="font-mono text-mbc-bluel text-xs">{i.sku}</span></td>
                  <td><span className="truncate inline-block max-w-[220px]" title={i.nome}>{i.nome}</span></td>
                  <td className="text-mbc-mute text-xs">{i.fornecedor??'—'}</td>
                  <td className="text-center"><span className="badge bg-mbc-orange/25 text-mbc-orangel">{i.classe}</span></td>
                  <td className="num">{fmtInt(i.estoque_total)}</td>
                  <td className="num text-mbc-mute">{i.cobertura_dias!=null?`${i.cobertura_dias}d`:'—'}</td>
                  <td className="num text-emerald-300">{fmtInt(i.sugestao_compra)}</td>
                  <td className="num font-semibold text-rose-300">{fmtMoney(Number(i.valor_perdido))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
